import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PRODUCT_TYPES } from '../constants';
import { formatPrice, calculateTotalPrice } from './index';

// Ana Renk - Siyah
const PRIMARY_COLOR = [26, 26, 26]; // #1a1a1a
const SECONDARY_COLOR = [17, 17, 17]; // #111111
const LIGHT_BG = [245, 245, 245]; // #f5f5f5
const GRAY_TEXT = [100, 100, 100];

// Türkçe karakter temizleme fonksiyonu
const cleanTurkishChars = (text) => {
  return (text || '').replace(/[ğ]/g, 'g')
    .replace(/[Ğ]/g, 'G')
    .replace(/[ü]/g, 'u')
    .replace(/[Ü]/g, 'U')
    .replace(/[ş]/g, 's')
    .replace(/[Ş]/g, 'S')
    .replace(/[ı]/g, 'i')
    .replace(/[İ]/g, 'I')
    .replace(/[ö]/g, 'o')
    .replace(/[Ö]/g, 'O')
    .replace(/[ç]/g, 'c')
    .replace(/[Ç]/g, 'C');
};

// Modern Header çiz
const drawHeader = (doc, title, subTitle = '') => {
  // Üst banner
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, 210, 35, 'F');

  // Firma adı
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Stil Perde', 20, 18);

  // Alt başlık / Müşteri Adı
  if (subTitle) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(cleanTurkishChars(subTitle).toUpperCase(), 20, 29);
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(cleanTurkishChars(title), 20, 28);
  }

  // Sağ taraf - İletişim
  doc.setFontSize(9);
  doc.text('Tel: +90 553 812 04 51', 190, 14, { align: 'right' });
  doc.text('stilperdeyalova@gmail.com', 190, 22, { align: 'right' });
  doc.text('Suleyman Bey, Mimar Sinan Cd. No:64/B Merkez Yalova', 190, 30, { align: 'right' });

  // Reset text color
  doc.setTextColor(0, 0, 0);
};

// Modern Footer çiz
const drawFooter = (doc) => {
  const pageHeight = doc.internal.pageSize.height;

  // Alt çizgi
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.5);
  doc.line(20, pageHeight - 25, 190, pageHeight - 25);

  // Footer text
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Stil Perde - Kaliteli Perde Cozumleri', 105, pageHeight - 18, { align: 'center' });
  doc.text('Bu belge bilgisayar ortaminda olusturulmustur.', 105, pageHeight - 12, { align: 'center' });

  // Sayfa numarası
  const pageCount = doc.internal.getNumberOfPages();
  doc.text(`Sayfa ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`, 190, pageHeight - 12, { align: 'right' });

  doc.setTextColor(0, 0, 0);
};

// Metni satırlara böl
const splitTextToLines = (text, maxCharsPerLine) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
      currentLine = (currentLine + ' ' + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines;
};

// Müşteri bilgileri kutusu
const drawCustomerInfoBox = (doc, customerInfo, startY) => {
  const cleanName = cleanTurkishChars(customerInfo.name || '');
  const cleanAddress = cleanTurkishChars(customerInfo.address || '');

  // Adresi satırlara böl (maks 60 karakter per satır)
  const addressLines = splitTextToLines(cleanAddress, 60);
  const extraLines = Math.max(0, addressLines.length - 1);
  const boxHeight = 45 + (extraLines * 7);

  // Kutu arka planı
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, startY, 180, boxHeight, 3, 3, 'F');

  // Sol taraf - Müşteri bilgileri
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MUSTERI BILGILERI', 20, startY + 10);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(`Ad Soyad: ${cleanName}`, 20, startY + 20);
  doc.text(`Telefon: ${customerInfo.phone1 || '-'}`, 20, startY + 28);
  
  if (customerInfo.tcNo) {
    doc.text(`TC / Vergi No: ${customerInfo.tcNo}`, 100, startY + 28);
  }

  // Adres satırları
  let addressY = startY + 36;
  addressLines.forEach((line, index) => {
    const prefix = index === 0 ? 'Adres: ' : '       ';
    doc.text(`${prefix}${line}`, 20, addressY);
    addressY += 7;
  });

  // Sağ taraf - Tarihler
  if (customerInfo.orderDate || customerInfo.deliveryDate) {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_TEXT);

    if (customerInfo.orderDate) {
      const orderDate = customerInfo.orderDate?.toDate ? customerInfo.orderDate.toDate() : new Date(customerInfo.orderDate);
      doc.text(`Siparis: ${orderDate.toLocaleDateString('tr-TR')}`, 190, startY + 20, { align: 'right' });
    }

    if (customerInfo.deliveryDate) {
      const deliveryDate = customerInfo.deliveryDate?.toDate ? customerInfo.deliveryDate.toDate() : new Date(customerInfo.deliveryDate);
      doc.text(`Teslimat: ${deliveryDate.toLocaleDateString('tr-TR')}`, 190, startY + 28, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 8;
};

// Toplam tutar kutusu - Sağda alt alta format
const drawTotalBox = (doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, startY, showPaymentInfo = false, paidAmount = 0, remainingAmount = 0, showDetailedPricing = true, manualGrandTotal = null) => {
  const vYuzde = parseFloat(vadeliYuzde) || 0;
  const gIskonto = parseFloat(genelIskontoYuzde) || 0;

  const hasVade = vYuzde > 0;
  const hasIskonto = gIskonto > 0;
  const hasProductDiscount = discountedTotalAmount !== totalAmount && discountedTotalAmount > 0;

  // Hesaplamalar
  const vadeliToplam = totalAmount + (totalAmount * vYuzde / 100);
  const iskontoluToplam = totalAmount - (totalAmount * gIskonto / 100);

  // Hiyerarşi (Genel Toplam): Manuel Giriş > Vade Farkı > Genel İskonto > Ürün İndirimi > Ara Toplam
  let grandTotal = totalAmount;
  if (manualGrandTotal !== null && manualGrandTotal !== undefined && manualGrandTotal !== '') {
    grandTotal = parseFloat(manualGrandTotal);
  } else if (hasVade) {
    grandTotal = vadeliToplam;
  } else if (hasIskonto) {
    grandTotal = iskontoluToplam;
  } else if (hasProductDiscount) {
    grandTotal = discountedTotalAmount;
  }

  // Kutu yüksekliğini hesapla
  let itemCount = 1; // Sadece Genel toplam (alt sınır)
  if (showDetailedPricing) {
    itemCount += 1; // Ara toplam
    if (hasProductDiscount) itemCount++;
    if (hasIskonto) itemCount += 2; // % + tutar
    if (hasVade) itemCount += 2; // % + tutar
  }

  if (showPaymentInfo) {
    itemCount += 2; // Ödenen + Kalan
  }
  const boxHeight = 20 + (itemCount * 10);

  // Ana kutu
  doc.setFillColor(...PRIMARY_COLOR);
  doc.roundedRect(100, startY, 95, boxHeight, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  let lineY = startY + 12;

  if (showDetailedPricing) {
    // Ara Toplam (Brüt)
    doc.text('Ara Toplam:', 105, lineY);
    doc.text(`${formatPrice(totalAmount)} TL`, 190, lineY, { align: 'right' });
    lineY += 9;

    // Ürün İndirimli Toplam
    if (hasProductDiscount) {
      doc.text('Urun Indirimli Toplam:', 105, lineY);
      doc.text(`${formatPrice(discountedTotalAmount)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Genel İskonto
    if (hasIskonto) {
      doc.text(`Genel Iskonto Tutari (%${genelIskontoYuzde}):`, 105, lineY);
      doc.text(`-${formatPrice(totalAmount * gIskonto / 100)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
      
      doc.text('Iskontolu Toplam:', 105, lineY);
      doc.text(`${formatPrice(iskontoluToplam)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Vade Farkı
    if (hasVade) {
      doc.text(`Vade Farki (%${vadeliYuzde}):`, 105, lineY);
      doc.text(`+${formatPrice(totalAmount * vYuzde / 100)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;

      doc.text('Vadeli Toplam:', 105, lineY);
      doc.text(`${formatPrice(vadeliToplam)} TL`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Ayırıcı çizgi (sadece detaylı görünümde ara toplamlar varsa)
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);
  }


  // Genel toplam
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('GENEL TOPLAM (Net):', 105, lineY + 6);
  doc.text(`${formatPrice(grandTotal)} TL`, 190, lineY + 6, { align: 'right' });
  
  if (manualGrandTotal !== null && manualGrandTotal !== undefined && manualGrandTotal !== '') {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('(Elle Duzeltilmis)', 105, lineY + 11);
  }
  
  lineY += 14;

  // Ödenen ve Kalan bilgileri (sadece Müşteri PDF için)
  if (showPaymentInfo) {
    // Ayırıcı çizgi
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    // Ödenen
    doc.text('Odenen:', 105, lineY + 4);
    doc.text(`${formatPrice(paidAmount)} TL`, 190, lineY + 4, { align: 'right' });
    lineY += 9;

    // Kalan
    const isPaymentComplete = Math.abs(remainingAmount) < 0.01 || remainingAmount <= 0;
    doc.setFont('helvetica', 'bold');
    doc.text('Kalan:', 105, lineY + 4);
    if (isPaymentComplete) {
      doc.text('ODEME TAMAMLANDI', 190, lineY + 4, { align: 'right' });
    } else {
      doc.text(`${formatPrice(remainingAmount)} TL`, 190, lineY + 4, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 10;
};

// Müşteri PDF tablo verisini oluşturur
const buildCustomerTableData = (orders, includeMeasurements = true) => {
  const head = includeMeasurements
    ? [['Urun Tipi', 'Oda', 'Urun Adi', 'Olculer(cm)', 'Pile Modeli', 'Adet', 'Toplam Ucret (TL)', 'Indirimli (TL)']]
    : [['Urun Tipi', 'Oda', 'Urun Adi', 'Pile Modeli', 'Adet', 'Toplam Ucret (TL)', 'Indirimli (TL)']];

  const body = orders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');
    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    const cleanPileModel = cleanTurkishChars(order.pileModel || '');
    const discountRate = parseFloat(order.discount) || 0;

    // Plise için özel satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      const pliseRows = order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisistem)' : '';
        const cleanProductName = item.multisistem ? (p1Base + p2Base + cleanTurkishChars(ms)) : p1Base;

        const row = [
          cleanProductType,
          cleanRoomName,
          cleanProductName
        ];

        if (includeMeasurements) {
          row.push(`${item.width || '-'} x ${item.height || '-'}`);
        }

        const rowTotal = parseFloat(item.rowTotal) || 0;
        const discountedRowTotal = rowTotal * (1 - discountRate / 100);

        row.push(
          '-', // Plise için pile modeli yok
          item.quantity || '1',
          formatPrice(rowTotal),
          discountRate > 0 ? formatPrice(discountedRowTotal) : '-'
        );

        return row;
      });

      const pliseNoteItems = [];
      const pliseAccList = Array.isArray(order.accessories) && order.accessories.length > 0
        ? order.accessories
        : (Array.isArray(order.kanatAccessories) ? order.kanatAccessories : []);
      if (pliseAccList.length > 0) {
        const accSummary = pliseAccList.map(a => `${a.name || 'Aksesuar'} (${a.quantity || 1} ad)`).join(', ');
        pliseNoteItems.push(cleanTurkishChars(`Aksesuarlar: ${accSummary}`));
      }
      if (order.notes && order.notes.trim()) {
        pliseNoteItems.push(cleanTurkishChars(`Siparis Notu: ${order.notes.trim()}`));
      }

      if (pliseNoteItems.length > 0) {
        const colSpanCount = includeMeasurements ? 8 : 7;
        const noteContent = pliseNoteItems.length === 1
          ? `Not: ${pliseNoteItems[0]}`
          : `Not:\n${pliseNoteItems.map(item => `  - ${item}`).join('\n')}`;

        pliseRows.push([
          {
            content: noteContent,
            colSpan: colSpanCount,
            styles: {
              halign: 'left',
              fontStyle: 'italic',
              fontSize: 8,
              textColor: [71, 85, 105],
              fillColor: [248, 250, 252],
              cellPadding: { top: 2.5, bottom: 2.5, left: 8, right: 8 }
            }
          }
        ]);
      }

      return pliseRows;
    }

    // Diğer ürün tipleri için tek satır
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    const row = [
      cleanProductType,
      cleanRoomName,
      cleanProductName
    ];

    if (includeMeasurements) {
      row.push(`${order.width || ''} x ${order.height || ''}`);
    }

    row.push(
      cleanPileModel,
      order.quantity || '1',
      formatPrice(order.totalPrice || 0),
      order.discountedPrice && parseFloat(order.discount) > 0 ? formatPrice(order.discountedPrice) : '-'
    );

    // Ray Sistemi için kullanılan metriği olculer sütununda göster (Müşteri PDF)
    if (order.productType === 'raySistemi' && includeMeasurements) {
      row[3] = `${order.width || ''} cm`; // Boy yok
      const usedMeter = order.usedMeter ? `${order.usedMeter} m` : '-';
      const olcu = order.olcuTamamlama ? ` (${order.olcuTamamlama})` : '';
      row[4] = cleanTurkishChars(order.olcuTamamlama || '-');
      row[3] = `${order.width || ''} cm\n${usedMeter}${olcu}`;
    }

    // Rustik için detayları göster
    if (order.productType === 'rustik' && includeMeasurements) {
      row[3] = `${order.width || ''} cm`; // Boy yok
      const usedCm = order.usedCm ? `${order.usedCm} cm` : '-';
      row[3] = `En: ${order.width || ''} cm\nKul: ${usedCm}`;

      let ayakDetay = cleanTurkishChars(order.ayakModel || '-');
      if (order.ayakSayisi) {
        ayakDetay += `\nAdet: ${order.ayakSayisi}`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Ek)`;
      }
      if (order.ayakCap || order.ayakRenkKodu) {
        ayakDetay += `\n(${order.ayakCap || ''} ${cleanTurkishChars(order.ayakRenkKodu || '')})`;
      }
      row[4] = ayakDetay;
    }

    const orderRows = [row];

    // Özel Tasarım, Astar, Aksesuar ve Sipariş Notlarını siparişin altına not olarak ekle
    const noteItems = [];
    if (order.productType === 'kanat') {
      if ((order.hasOzelTasarim || !!order.ozelTasarimProductName) && order.ozelTasarimProductName) {
        let ozelName = order.ozelTasarimProductName;
        if (order.ozelTasarimProductVariant) ozelName += ` - ${order.ozelTasarimProductVariant}`;
        let ozelDetails = `Ozel Tasarim: ${ozelName}`;
        if (includeMeasurements && order.ozelTasarimWidth && order.ozelTasarimHeight) {
          ozelDetails += ` (${order.ozelTasarimWidth}x${order.ozelTasarimHeight} cm`;
          if (order.ozelTasarimPileModel) ozelDetails += `, ${order.ozelTasarimPileModel}`;
          if (order.ozelTasarimQuantity) ozelDetails += `, ${order.ozelTasarimQuantity} Adet`;
          ozelDetails += `)`;
        } else if (!includeMeasurements && (order.ozelTasarimPileModel || order.ozelTasarimQuantity)) {
          const parts = [];
          if (order.ozelTasarimPileModel) parts.push(order.ozelTasarimPileModel);
          if (order.ozelTasarimQuantity) parts.push(`${order.ozelTasarimQuantity} Adet`);
          ozelDetails += ` (${parts.join(', ')})`;
        }
        const ozelTotal = order.ozelTasarimTotalPrice || ((order.hasOzelTasarim || !!order.ozelTasarimProductName) ? calculateTotalPrice(order.ozelTasarimUsedM2, order.ozelTasarimUnitPrice, order.ozelTasarimExtraPrice || 0) * (parseInt(order.ozelTasarimQuantity) || 1) : 0);
        if (ozelTotal) {
          ozelDetails += ` - ${formatPrice(ozelTotal)} TL`;
        }
        noteItems.push(cleanTurkishChars(ozelDetails));
      }

      if ((order.hasAstar || !!order.astarProductName) && (order.astarProductName || order.astarUnitPrice)) {
        let astarText = `Astar: ${order.astarProductName || 'Standart Astar'}`;
        if (order.astarProductVariant) astarText += ` - ${order.astarProductVariant}`;
        if (order.astarPrice) astarText += ` (${formatPrice(order.astarPrice)} TL)`;
        noteItems.push(cleanTurkishChars(astarText));
      }
    }

    if (order.productType === 'tul' && order.hasAltEtekKatlama) {
      noteItems.push(cleanTurkishChars('Alt Etek Katlama'));
    }

    const accList = Array.isArray(order.accessories) && order.accessories.length > 0
      ? order.accessories
      : (Array.isArray(order.kanatAccessories) ? order.kanatAccessories : []);

    if (accList.length > 0) {
      const accSummary = accList.map(a => `${a.name || 'Aksesuar'} (${a.quantity || 1} ad)`).join(', ');
      noteItems.push(cleanTurkishChars(`Aksesuarlar: ${accSummary}`));
    }

    if (order.notes && order.notes.trim()) {
      noteItems.push(cleanTurkishChars(`Siparis Notu: ${order.notes.trim()}`));
    }

    if (noteItems.length > 0) {
      const colSpanCount = includeMeasurements ? 8 : 7;
      const noteContent = noteItems.length === 1
        ? `Not: ${noteItems[0]}`
        : `Not:\n${noteItems.map(item => `  - ${item}`).join('\n')}`;

      orderRows.push([
        {
          content: noteContent,
          colSpan: colSpanCount,
          styles: {
            halign: 'left',
            fontStyle: 'italic',
            fontSize: 8,
            textColor: [71, 85, 105],
            fillColor: [248, 250, 252],
            cellPadding: { top: 2.5, bottom: 2.5, left: 8, right: 8 }
          }
        }
      ]);
    }

    return orderRows;
  });

  return { head, body };
};

// Ana Müşteri PDF oluşturma
const generateCustomerPDFBase = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, options = {}) => {
  const manualGrandTotal = options.manualGrandTotal || null;
  const includeMeasurements = options.includeMeasurements !== false;
  const title = options.title || 'Musteri Siparisi';
  const showPaymentInfo = options.showPaymentInfo || false;
  const showDetailedPricing = options.showDetailedPricing !== false; // Varsayılan true
  const paidAmount = options.paidAmount || 0;
  const remainingAmount = options.remainingAmount || 0;

  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, title);

  // Müşteri bilgileri
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş tablosu başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY);
  currentY += 8;

  // Tablo verilerini hazırla
  const { head, body } = buildCustomerTableData(orders, includeMeasurements);

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head,
    body,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center'
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: includeMeasurements
      ? {
        0: { cellWidth: 22 },  // Urun Tipi
        1: { cellWidth: 18 },  // Oda
        2: { cellWidth: 25 },  // Urun Adi
        3: { cellWidth: 20 },  // Olculer(cm)
        4: { cellWidth: 25 },  // Pile Modeli
        5: { cellWidth: 12, halign: 'center' },  // Adet
        6: { cellWidth: 28, halign: 'right' },   // Toplam Ucret
        7: { cellWidth: 25, halign: 'right' }    // Indirimli
      }
      : {
        0: { cellWidth: 25 },  // Urun Tipi
        1: { cellWidth: 20 },  // Oda
        2: { cellWidth: 30 },  // Urun Adi
        3: { cellWidth: 28 },  // Pile Modeli
        4: { cellWidth: 15, halign: 'center' },  // Adet
        5: { cellWidth: 28, halign: 'right' },   // Toplam Ucret
        6: { cellWidth: 25, halign: 'right' }    // Indirimli
      },
    pageBreak: 'auto',
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, title + ' (Devam)');
      }
    }
  });

  // Toplam tutar kutusu
  let finalY = doc.lastAutoTable.finalY + 10;

  // Toplam kutusunun tahmini yüksekliğini hesapla
  const hasVadeli = vadeliYuzde && parseFloat(vadeliYuzde) > 0;
  let estimatedBoxItems = 1; // Genel toplam
  if (showDetailedPricing) {
    estimatedBoxItems += 1; // Ara toplam
    if (discountedTotalAmount !== totalAmount) estimatedBoxItems++;
    if (vadeliYuzde && parseFloat(vadeliYuzde) > 0) estimatedBoxItems += 2;
    if (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) estimatedBoxItems += 2;
  }
  if (showPaymentInfo) estimatedBoxItems += 2;
  const estimatedBoxHeight = 20 + (estimatedBoxItems * 10);

  // Sayfa kontrolü - footer için 35px, kutu için tahmini yükseklik
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + estimatedBoxHeight > pageHeight - 40) {
    doc.addPage();
    drawHeader(doc, title);
    finalY = 50;
  }

  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, showPaymentInfo, paidAmount, remainingAmount, showDetailedPricing, manualGrandTotal);

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Müşteri PDF oluşturma
export const generateCustomerPDF = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0, manualGrandTotal = null) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, {
    includeMeasurements: true,
    title: 'Siparis Detayi',
    showPaymentInfo: true,
    showDetailedPricing: false, // Müşteri detayda detaylı fiyat gizle
    paidAmount,
    remainingAmount,
    manualGrandTotal
  });
};

// Müşteri PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0, manualGrandTotal = null) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Siparis Detayi');
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, true);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 25 }, 3: { cellWidth: 20 }, 4: { cellWidth: 25 }, 5: { cellWidth: 12, halign: 'center' }, 6: { cellWidth: 28, halign: 'right' }, 7: { cellWidth: 25, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Siparis Detayi (Devam)'); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 80 > pageHeight - 40) { doc.addPage(); drawHeader(doc, 'Siparis Detayi'); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, true, paidAmount, remainingAmount, false, manualGrandTotal);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Müşteri (Sipariş öncesi) PDF oluşturma - en/boy bilgisi olmadan
export const generateCustomerPreOrderPDF = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, manualGrandTotal = null) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, {
    includeMeasurements: false,
    title: 'Teklif Formu',
    showPaymentInfo: false,
    showDetailedPricing: true, // Teklif formunda detaylı fiyat göster
    manualGrandTotal
  });
};

// Müşteri (Sipariş öncesi) PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPreOrderPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, manualGrandTotal = null) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Teklif Formu');
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('SIPARIS DETAYLARI', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, false);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 20 }, 2: { cellWidth: 30 }, 3: { cellWidth: 28 }, 4: { cellWidth: 15, halign: 'center' }, 5: { cellWidth: 28, halign: 'right' }, 6: { cellWidth: 25, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Teklif Formu (Devam)'); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 60 > pageHeight - 40) { doc.addPage(); drawHeader(doc, 'Teklif Formu'); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, false, 0, 0, true, manualGrandTotal);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Atölye PDF için ekstra satırları (Özel Tasarım, Astar, Aksesuar) derler
const buildWorkshopOrderExtraLines = (order) => {
  const extraItems = [];

  if (order.productType === 'kanat') {
    if ((order.hasOzelTasarim || !!order.ozelTasarimProductName) && order.ozelTasarimProductName) {
      let ozelName = order.ozelTasarimProductName;
      if (order.ozelTasarimProductVariant) ozelName += ` - ${order.ozelTasarimProductVariant}`;
      extraItems.push({
        type: 'ozelHeader',
        text: '--- OZEL TASARIM ---'
      });
      extraItems.push({
        type: 'ozelProduct',
        text: `Urun: ${cleanTurkishChars(ozelName).substring(0, 28)}`
      });
      if (order.ozelTasarimPileModel) {
        extraItems.push({
          type: 'ozelPile',
          text: `Pile: ${cleanTurkishChars(order.ozelTasarimPileModel).substring(0, 25)}`
        });
      }
      extraItems.push({
        type: 'ozelDims',
        text: `En: ${order.ozelTasarimWidth || '-'} cm  Boy: ${order.ozelTasarimHeight || '-'} cm  Adet: ${order.ozelTasarimQuantity || '1'}`
      });
    }

    if ((order.hasAstar || !!order.astarProductName) && (order.astarProductName || order.astarUnitPrice)) {
      let astarName = order.astarProductName || 'Standart Astar';
      if (order.astarProductVariant) astarName += ` - ${order.astarProductVariant}`;
      extraItems.push({
        type: 'astar',
        text: `Astar: ${cleanTurkishChars(astarName).substring(0, 28)}`
      });
    }
  }

  const accList = Array.isArray(order.accessories) && order.accessories.length > 0
    ? order.accessories
    : (Array.isArray(order.kanatAccessories) ? order.kanatAccessories : []);

  if (accList.length > 0) {
    const accSummary = accList.map(a => `${a.name || 'Aksesuar'} (${a.quantity || 1} ad)`).join(', ');
    extraItems.push({
      type: 'accessory',
      text: `Aksesuar: ${cleanTurkishChars(accSummary).substring(0, 30)}`
    });
  }

  return extraItems;
};

// Bir atölye kutusunun dinamik yüksekliğini hesaplar
const calcWorkshopBoxHeight = (order) => {
  const extraItems = buildWorkshopOrderExtraLines(order);
  const cleanNotes = cleanTurkishChars(order.notes || '');
  const noteLines = cleanNotes ? splitTextToLines(cleanNotes, 42).slice(0, 2) : [];

  let height = 48;

  extraItems.forEach(item => {
    if (item.type === 'ozelHeader') height += 5;
    else if (item.type === 'ozelProduct') height += 5;
    else if (item.type === 'ozelPile') height += 5;
    else if (item.type === 'ozelDims') height += 5;
    else if (item.type === 'astar') height += 5;
    else if (item.type === 'accessory') height += 5;
  });

  if (order.productType === 'tul' && order.hasAltEtekKatlama) {
    height += 6;
  }

  if (noteLines.length > 0) {
    height += 6 + (noteLines.length * 4.5);
  }

  return Math.max(63, height + 6);
};

// Atölye kutusunu çizen yardımcı fonksiyon
const drawWorkshopBox = (doc, order, x, y, width, height, customerName) => {
  // Kutu arka planı
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(x, y, width, height, 3, 3, 'F');

  // Kutu kenarlığı
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, width, height, 3, 3, 'S');

  // Üst şerit - müşteri adı + oda adı
  doc.setFillColor(...PRIMARY_COLOR);
  doc.roundedRect(x, y, width, 14, 3, 3, 'F');
  doc.rect(x, y + 7, width, 7, 'F');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);

  const cleanCustomerName = cleanTurkishChars(customerName || '');
  const cleanRoomName = cleanTurkishChars(order.roomName || '');
  doc.text(cleanCustomerName.substring(0, 25), x + 4, y + 9);
  doc.setFontSize(8);
  doc.text(cleanRoomName, x + width - 4, y + 9, { align: 'right' });

  // Ana İçerik
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9.5);

  let fullProductName = order.productName || '';
  if (order.productVariant) fullProductName += ` - ${order.productVariant}`;
  const cleanProductName = cleanTurkishChars(fullProductName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');
  const cleanPileModel = cleanTurkishChars(order.pileModel || '');
  const cleanNotes = cleanTurkishChars(order.notes || '');

  let textY = y + 19;
  doc.text(`Urun: ${cleanProductName.substring(0, 28)}`, x + 4, textY);
  textY += 6;
  doc.text(`Pile: ${cleanPileModel.substring(0, 25)}`, x + 4, textY);
  textY += 6;
  doc.setFont('helvetica', 'bold');
  let dimText = `En: ${order.width || '-'} cm  Boy: ${order.height || '-'} cm  Adet: ${order.quantity || '1'}`;
  if (order.usedM2) dimText += ` (${order.usedM2}m)`;
  doc.text(dimText, x + 4, textY);
  doc.setFont('helvetica', 'normal');

  // Ekstra öğeler (Özel Tasarım, Astar, Aksesuar)
  const extraItems = buildWorkshopOrderExtraLines(order);
  extraItems.forEach(item => {
    textY += 5;
    if (item.type === 'ozelHeader') {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...PRIMARY_COLOR);
      doc.text(item.text, x + 4, textY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(0, 0, 0);
    } else if (item.type === 'ozelDims') {
      doc.setFont('helvetica', 'bold');
      let ozelDimsText = item.text;
      if (order.ozelTasarimUsedM2) ozelDimsText += ` (${order.ozelTasarimUsedM2}m)`;
      doc.text(ozelDimsText, x + 4, textY);
      doc.setFont('helvetica', 'normal');
    } else {
      doc.text(item.text, x + 4, textY);
    }
  });

  // Alt Etek Katlama Notu (Not alanının hemen üzerine)
  if (order.productType === 'tul' && order.hasAltEtekKatlama) {
    textY += 5.5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('Alt Etek Katlama Olacak', x + 4, textY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  }

  // Not alanı
  if (cleanNotes) {
    const noteLines = splitTextToLines(cleanNotes, 42).slice(0, 2);
    textY += 5;
    doc.setFontSize(8.5);
    doc.setTextColor(...GRAY_TEXT);
    doc.text('Not:', x + 4, textY);
    noteLines.forEach((line) => {
      textY += 4.5;
      doc.text(line, x + 4, textY);
    });
  }

  doc.setTextColor(0, 0, 0);
};

// Atölye PDF oluşturma
export const generateWorkshopPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const tulKanatOrders = orders.filter(order =>
    order.productType === 'tul' || order.productType === 'kanat'
  );

  if (tulKanatOrders.length === 0) {
    alert('Atölye PDF için Tül veya Kanat ürünü bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header - Müşteri adını büyük başlık olarak yazdır
  drawHeader(doc, 'Atolye Siparisi', customerInfo.name);

  let currentY = 45;
  const boxWidth = 90;
  const margin = 6;

  for (let i = 0; i < tulKanatOrders.length; i += 2) {
    const orderA = tulKanatOrders[i];
    const orderB = tulKanatOrders[i + 1] || null;

    const heightA = calcWorkshopBoxHeight(orderA);
    const heightB = orderB ? calcWorkshopBoxHeight(orderB) : 0;
    const rowHeight = Math.max(heightA, heightB);

    if (currentY + rowHeight > 270) {
      doc.addPage();
      drawHeader(doc, 'Atolye Siparisi', customerInfo.name);
      currentY = 45;
    }

    // Sol kutu
    drawWorkshopBox(doc, orderA, 12, currentY, boxWidth, rowHeight, customerInfo.name);

    // Sağ kutu (varsa)
    if (orderB) {
      drawWorkshopBox(doc, orderB, 12 + boxWidth + margin, currentY, boxWidth, rowHeight, customerInfo.name);
    }

    currentY += rowHeight + 8; // Satır arası boşluk
  }

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Montaj PDF oluşturma
export const generateInstallationPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Montaj Siparisi');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş listesi başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MONTAJ LISTESI', 20, currentY);
  currentY += 8;

  // Siparişleri oda gruplarına göre sırala
  const sortedOrders = [...orders].sort((a, b) => {
    const roomGroupA = (a.roomName || '').substring(0, 3).toUpperCase();
    const roomGroupB = (b.roomName || '').substring(0, 3).toUpperCase();

    if (roomGroupA !== roomGroupB) {
      return roomGroupA.localeCompare(roomGroupB, 'tr-TR');
    }

    const roomCompare = (a.roomName || '').localeCompare(b.roomName || '', 'tr-TR');
    if (roomCompare !== 0) return roomCompare;

    return (a.productType || '').localeCompare(b.productType || '', 'tr-TR');
  });

  // Tablo verileri
  const tableData = sortedOrders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');
    const cleanRoomName = cleanTurkishChars(order.roomName || '');

    // Plise için detaylı satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisistem)' : '';
        const namePart = item.multisistem ? (p1Base + p2Base + ms) : p1Base;
        const dimensionPart = ` [${item.width} x ${item.height} cm] - ${item.quantity || 1} Adet`;
        const fullProductName = namePart + cleanTurkishChars(dimensionPart);

        return [
          cleanRoomName,
          cleanProductType,
          fullProductName
        ];
      });
    }

    // Diğer ürünler için
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    let cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

    // En ve boy bilgisi ekleme
    let dimText = '';
    if (order.width && order.height) {
      dimText = ` [${order.width} x ${order.height} cm]`;
    } else if (order.width) {
      dimText = ` [${order.width} cm]`;
    } else if (order.height) {
      dimText = ` [${order.height} cm]`;
    }

    if (order.quantity) {
      dimText += ` - ${order.quantity} Adet`;
    }

    if (dimText) {
      cleanProductName += cleanTurkishChars(dimText);
    }

    if (order.productType === 'kanat') {
      const extraList = [];
      if ((order.hasOzelTasarim || !!order.ozelTasarimProductName) && order.ozelTasarimProductName) {
        let ozel = `Ozel Tasarim: ${order.ozelTasarimProductName}`;
        if (order.ozelTasarimProductVariant) ozel += ` - ${order.ozelTasarimProductVariant}`;
        if (order.ozelTasarimWidth && order.ozelTasarimHeight) {
          ozel += ` [${order.ozelTasarimWidth} x ${order.ozelTasarimHeight} cm]`;
        } else if (order.ozelTasarimWidth) {
          ozel += ` [${order.ozelTasarimWidth} cm]`;
        }
        if (order.ozelTasarimQuantity) {
          ozel += ` - ${order.ozelTasarimQuantity} Adet`;
        }
        extraList.push(cleanTurkishChars(ozel));
      }
      if ((order.hasAstar || !!order.astarProductName) && (order.astarProductName || order.astarUnitPrice)) {
        extraList.push(cleanTurkishChars(`Astar: ${order.astarProductName || 'Standart Astar'}`));
      }
      if (extraList.length > 0) {
        cleanProductName += `\n${extraList.join('\n')}`;
      }
    }

    return [[
      cleanRoomName,
      cleanProductType,
      cleanProductName
    ]];
  });

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head: [['Oda', 'Urun Tipi', 'Urun Adi']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10
    },
    styles: {
      fontSize: 10,
      cellPadding: 5
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 35 },
      2: { cellWidth: 105 }
    },
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, 'Montaj Siparisi (Devam)');
      }
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Firma PDF oluşturma
export const generateFirmaPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Müşteri bilgileri veya sipariş bulunamadı!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Firma Siparisi');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Siparişleri ürün tipine göre ayır
  const tulKanatOrders = orders.filter(order => order.productType === 'tul' || order.productType === 'kanat');
  const otherOrders = orders.filter(order => !['tul', 'kanat'].includes(order.productType));

  // Tül ve Kanat tablosu
  if (tulKanatOrders.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('TUL VE KANAT SIPARISLERI', 20, currentY);

    const tulKanatTableData = tulKanatOrders.flatMap((order) => {
      const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');

      let productName = order.productName || '';
      if (order.productType === 'plise') {
        productName = order.pliseProduct1Name || '';
        if (order.pliseProduct2Name) {
          productName += ` + ${order.pliseProduct2Name}`;
        }
      } else if (order.productVariant) {
        productName += ` - ${order.productVariant}`;
      }

      const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

      const rows = [
        [
          cleanProductType,
          cleanProductName,
          `${order.usedM2 || ''} m`,
          order.quantity || '1'
        ]
      ];

      if (order.productType === 'kanat') {
        if ((order.hasOzelTasarim || !!order.ozelTasarimProductName) && order.ozelTasarimProductName) {
          let ozelName = order.ozelTasarimProductName;
          if (order.ozelTasarimProductVariant) ozelName += ` - ${order.ozelTasarimProductVariant}`;
          rows.push([
            cleanProductType + ' (Ozel)',
            cleanTurkishChars(ozelName),
            `${order.ozelTasarimUsedM2 || ''} m`,
            order.ozelTasarimQuantity || '1'
          ]);
        }
        if ((order.hasAstar || !!order.astarProductName) && (order.astarProductName || order.astarUnitPrice)) {
          let astarName = order.astarProductName || 'Standart Astar';
          if (order.astarProductVariant) astarName += ` - ${order.astarProductVariant}`;
          rows.push([
            'Astar',
            cleanTurkishChars(astarName),
            `${order.usedM2 || ''} m`,
            order.quantity || '1'
          ]);
        }
      }

      return rows;
    });

    autoTable(doc, {
      startY: currentY + 8,
      head: [['Urun Tipi', 'Urun Adi', 'Kullanilan m', 'Adet']],
      body: tulKanatTableData,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 60 },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' }
      },
      margin: { top: 45, right: 15, bottom: 35, left: 15 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, 'Firma Siparisi (Devam)');
        }
      }
    });

    currentY = doc.lastAutoTable.finalY + 15;
  }

  // Diğer ürünler tablosu
  if (otherOrders.length > 0) {
    if (currentY > 200) {
      doc.addPage();
      drawHeader(doc, 'Firma Siparisi (Devam)');
      currentY = 50;
    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('MEKANIK URUN SIPARISLERI', 20, currentY);

    const otherTableData = otherOrders.map((order) => {
      const cleanProductType = cleanTurkishChars(PRODUCT_TYPES[order.productType]?.label || order.productType || '');

      let productName = order.productName || '';
      if (order.productType === 'plise') {
        productName = order.pliseProduct1Name || '';
        if (order.pliseProduct2Name) {
          productName += ` + ${order.pliseProduct2Name}`;
        }
      } else if (order.productVariant) {
        productName += ` - ${order.productVariant}`;
      }

      const cleanProductName = cleanTurkishChars(productName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');

      let finalProductName = cleanProductName;
      if (order.productType === 'rustik') {
        let ayakDetay = ` [Ayak: ${cleanTurkishChars(order.ayakModel || '')}`;
        if (order.ayakRenkKodu) ayakDetay += ` - ${cleanTurkishChars(order.ayakRenkKodu)}`;
        ayakDetay += ` - ${order.ayakSayisi || 0} Adet`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Ek)`;
        ayakDetay += ']';
        finalProductName += ayakDetay;

        // Aksesuar ve Ray seçeneklerini de ekle
        if (Array.isArray(order.rustikAksesuarlar) && order.rustikAksesuarlar.length > 0) {
          finalProductName += '\n Aksesuarlar: ' + order.rustikAksesuarlar.map(a => `${cleanTurkishChars(a.name)} (${a.quantity})`).join(', ');
        }
        if (Array.isArray(order.rustikRaySecenekleri) && order.rustikRaySecenekleri.length > 0) {
          finalProductName += '\n Ray: ' + order.rustikRaySecenekleri.map(r => `${cleanTurkishChars(r.name)} (${r.quantity})`).join(', ');
        }
      }

      const isRay = order.productType === 'raySistemi';
      const isRustik = order.productType === 'rustik';

      const usedVal = isRay ? (order.usedMeter ? `${order.usedMeter} m` : '-')
        : isRustik ? (order.usedCm ? `${order.usedCm} cm` : '-')
          : (order.usedM2 ? `${order.usedM2} m2` : '-');

      const spec = isRay && order.olcuTamamlama ? `\n(${cleanTurkishChars(order.olcuTamamlama)})` : '';

      return [
        cleanProductType,
        finalProductName,
        `${order.width || ''}`,
        (isRay || isRustik) ? '-' : `${order.height || ''}`,
        `${usedVal}${spec}`,
        order.quantity || '1'
      ];
    });

    autoTable(doc, {
      startY: currentY + 8,
      head: [['Urun Tipi', 'Urun Adi', 'En (cm)', 'Boy (cm)', 'Kullanilan m2', 'Adet']],
      body: otherTableData,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 50 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 25, halign: 'center' },
        5: { cellWidth: 18, halign: 'center' }
      },
      margin: { top: 45, right: 15, bottom: 35, left: 15 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, 'Firma Siparisi (Devam)');
        }
      }
    });
  }

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};
